import { Button, Form } from "react-bootstrap";
import styled from "styled-components";

import { IconButton } from "@bagel-web/components";

import ParameterDetails from "./ParameterDetails";
import ParameterNameField from "./ParameterNameField";
import ParameterTypeSelect from "./ParameterTypeSelect";
import {
  addParameter,
  deleteParameter,
  FnParameterMap,
  renameParameter,
  setParameterRequired,
} from "../playgroundFunctions";

// Parameter blocks alternate background styling based on depth of nesting
const ParameterBlock = styled.div<{ $depth: number }>`
  background-color: ${({ $depth }) =>
    $depth % 2 === 0 ? "var(--aa-surface-bg)" : "var(--bs-card-bg)"};
`;

const ParameterMapEditor = ({
  path,
  noun,
  depth,
  map,
  onChange,
}: {
  path: string;
  noun: "argument" | "property";
  // Nesting depth at which this editor renders
  depth: number;
  map: FnParameterMap;
  onChange: (next: FnParameterMap) => void;
}) => {
  const entries = Object.entries(map.parameters);
  const nounLabel = noun === "argument" ? "Argument" : "Property";
  const addLabel = `Add ${nounLabel}`;
  return (
    <>
      {entries.map(([name, parameter], index) => {
        const blockPath = `${path}-${index}`;
        const required = map.required.includes(name);
        return (
          <ParameterBlock
            key={index}
            $depth={depth}
            className="border rounded p-2 mb-2"
            data-testid={blockPath}
          >
            <div className="d-flex align-items-center gap-2">
              <ParameterNameField
                id={`${blockPath}-name`}
                aria-label={`${nounLabel} name`}
                name={name}
                takenNames={
                  new Set(
                    entries.map(([key]) => key).filter((key) => key !== name)
                  )
                }
                onRename={(next) => onChange(renameParameter(map, name, next))}
              />
              <ParameterTypeSelect
                id={`${blockPath}-type`}
                aria-label={`${nounLabel} type`}
                parameter={parameter}
                onChange={(next) =>
                  onChange({
                    ...map,
                    parameters: { ...map.parameters, [name]: next },
                  })
                }
              />
              <Form.Check
                type="switch"
                id={`${blockPath}-required`}
                label="Required"
                className="text-nowrap"
                checked={required}
                onChange={() =>
                  onChange(setParameterRequired(map, name, !required))
                }
                data-testid={`${blockPath}-required`}
              />
              <IconButton
                className="bi-trash-fill"
                variant="secondary"
                tooltip={`Delete this ${noun}.`}
                data-testid={`${blockPath}-delete`}
                onClick={() => onChange(deleteParameter(map, name))}
              />
            </div>
            <ParameterDetails
              path={blockPath}
              depth={depth}
              parameter={parameter}
              onChange={(next) =>
                onChange({
                  ...map,
                  parameters: { ...map.parameters, [name]: next },
                })
              }
            />
          </ParameterBlock>
        );
      })}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => onChange(addParameter(map, noun))}
        data-testid={`${path}-add`}
      >
        <i className="bi-plus-lg me-1" />
        {addLabel}
      </Button>
    </>
  );
};

export default ParameterMapEditor;
